import { awsService } from '@/api';
import { Button, Icon, Typography } from '@/components/common';
import { Input } from '@/components/form';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { usePhotoEditor } from 'react-photo-editor';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import clsx from 'clsx';
import { urlToFile } from '@/lib/urlToFile';

interface ImageEditorProps {
	image: string;
	aspectRatio: number;
	width?: number;
	height?: number;
	hasDownload?: boolean;
	handleImageEdited: (newImage: string, oldImage: string) => void;
	hideImageEditor: () => void;
}

export const getCroppedImg = async (
	imageSrc: string,
	croppedAreaPixels: Area
): Promise<Blob> => {
	const image = await createImage(imageSrc);
	const canvas = document.createElement('canvas');
	canvas.width = croppedAreaPixels.width;
	canvas.height = croppedAreaPixels.height;
	const ctx = canvas.getContext('2d');

	if (!ctx) throw new Error('Canvas context not found');

	ctx.drawImage(
		image,
		croppedAreaPixels.x,
		croppedAreaPixels.y,
		croppedAreaPixels.width,
		croppedAreaPixels.height,
		0,
		0,
		croppedAreaPixels.width,
		croppedAreaPixels.height
	);

	return new Promise((resolve) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
		}, 'image/jpeg');
	});
};

const createImage = (url: string): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.crossOrigin = 'anonymous';
		image.src = url;
		image.onload = () => resolve(image);
		image.onerror = (error) => reject(error);
	});
};

export const ImageEditor: React.FC<ImageEditorProps> = ({
	image,
	aspectRatio,
	height,
	width,
	hasDownload,
	handleImageEdited,
	hideImageEditor,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const [file, setFile] = React.useState<File>();
	const [imageLoaded, setImageLoaded] = React.useState(false);

	const [crop, setCrop] = React.useState({ x: 0, y: 0 });
	const [isCropping, setIsCropping] = React.useState(false);
	const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(
		null
	);

	React.useEffect(() => {
		const fetchImage = async () => {
			try {
				const file = await urlToFile(image);
				setFile(file);
				setImageLoaded(true);
			} catch (error) {
				console.error('Error loading image:', error);
			}
		};
		image && fetchImage();
	}, [image]);

	const [croppableImage, setCroppableImage] = React.useState<string | null>(
		null
	);

	const {
		canvasRef,
		brightness,
		setBrightness,
		contrast,
		setContrast,
		saturate,
		setSaturate,
		grayscale,
		setGrayscale,
		rotate,
		setRotate,
		flipHorizontal,
		setFlipHorizontal,
		flipVertical,
		setFlipVertical,
		zoom,
		setZoom,
		handlePointerDown,
		handlePointerUp,
		handlePointerMove,
		handleWheel,
		downloadImage,
		generateEditedFile,
		// resetFilters,
		// imageSrc,
		// applyFilter,
	} = usePhotoEditor({ file });

	const handleRecortImage = () => {
		// const editedImage = canvasRef.current?.toDataURL(); // Imagen con filtros aplicados
		// setImageToCrop(editedImage);
		setIsCropping(true);

		const loadImage = async () => {
			const imageEdited = await generateEditedFile();
			if (imageEdited) {
				const url = URL.createObjectURL(imageEdited);
				setCroppableImage(url);
			} else {
				setCroppableImage(image);
			}
		};

		loadImage();

		// Cleanup: revocar objeto URL cuando se desmonte el componente
		return () => {
			if (croppableImage) {
				URL.revokeObjectURL(croppableImage);
			}
		};
	};

	const applyChangesOnClick = async () => {
		if (isCropping && croppableImage && croppedAreaPixels) {
			const blob = await getCroppedImg(croppableImage, croppedAreaPixels);

			const croppedFile = new File([blob], 'cropped.jpg', {
				type: 'image/jpeg',
			});
			try {
				const res = await awsService.uploadImage(croppedFile);
				handleImageEdited(res.data.data.location, image);
				hideImageEditor();
			} catch (error) {
				toastError(
					'An error occurred while applying the changes. Please try again.'
				);
			}
			// return;
		}

		const imageEdited = await generateEditedFile();
		try {
			if (imageEdited) {
				toastSuccess('Your changes have been successfully applied.');
				await new Promise((resolve) => setTimeout(resolve, 500));
				const res = await awsService.uploadImage(imageEdited);
				handleImageEdited(res.data.data.location, image);
				hideImageEditor();
			}
		} catch (error) {
			toastError(
				'An error occurred while applying the changes. Please try again.'
			);
		}
	};

	return (
		<div className="flex flex-col lg:flex-row gap-[22px]">
			<div className="bg-bg-white border border-grey-20 rounded-3xl py-8 px-6 w-full lg:w-[275px] lg:h-[684px] ">
				<Typography type="semibold-14" className="text-black mb-3 text-start">
					Settings
				</Typography>
				<div className="flex items-center gap-x-2">
					<Icon src={Icons.galleryEdit2} className="!size-6 !text-black" />
					<Typography type="regular-14" className="text-black">
						Format Options
					</Typography>
				</div>
				<div className="space-y-4 mt-6">
					<EditorOption
						title="Zoom"
						options={[
							{
								icon: Icons.searchZoomIn,
								value: zoom,
								name: 'searchZoomIn',
								onClick: () => setZoom(zoom + 0.1),
							},
							{
								icon: Icons.searchZoomOut,
								value: zoom,
								name: 'searchZoomOut',
								onClick: () => setZoom(zoom - 0.1),
							},
						]}
					/>
					<EditorOption
						title="Rotate"
						options={[
							{
								icon: Icons.rotateLeft,
								value: rotate,
								name: 'rotate',
								onClick: () => setRotate(rotate - 90),
							},
							{
								icon: Icons.rotateRight,
								value: rotate,
								name: 'rotateRight',
								onClick: () => setRotate(rotate + 90),
							},
							{
								icon: Icons.refresh2,
								value: 0,
								name: 'refresh2',
								onClick: () => setRotate(rotate + 180),
							},
						]}
					/>
					<div className="flex gap-x-10">
						<EditorOption
							title="Mirror"
							options={[
								{
									icon: Icons.flipRight,
									value: flipHorizontal,
									name: 'flipHorizontal',
									onClick: (e) => setFlipHorizontal(e),
								},
								{
									icon: Icons.flipVertical,
									value: flipVertical,
									name: 'flipVertical',
									onClick: (e) => setFlipVertical(e),
								},
							]}
						/>
						<EditorOption
							title="Crop"
							options={[
								{
									icon: Icons.cut,
									value: 0,
									name: 'cut',
									onClick: () => handleRecortImage(),
								},
								{
									icon: Icons.expand,
									value: 0,
									name: 'expand',
									onClick: () => handleRecortImage(),
								},
							]}
						/>
					</div>
				</div>
				<div className="flex items-center gap-x-2 mt-6">
					<Icon src={Icons.thermometer} className="!size-6 !text-black" />
					<Typography type="regular-14" className="text-black">
						Balance
					</Typography>
				</div>
				<div className="mt-4 space-y-3">
					<EditorOptionRange
						title="Saturate"
						value={saturate}
						onChange={(e) => setSaturate(e)}
					/>
					<EditorOptionRange
						title="Grayscale"
						value={grayscale}
						onChange={(e) => setGrayscale(e)}
					/>
				</div>

				<div className="flex items-center gap-x-2 mt-6">
					<Icon src={Icons.sun} className="!size-6 !text-black" />
					<Typography type="regular-14" className="text-black">
						Lightning
					</Typography>
				</div>
				<div className="mt-4 space-y-3">
					<EditorOptionRange
						title="Brightness"
						value={brightness}
						onChange={(e) => setBrightness(e)}
					/>
					<EditorOptionRange
						title="Contrast"
						value={contrast}
						onChange={(e) => setContrast(e)}
					/>
				</div>
			</div>
			<div className="bg-bg-white border border-grey-20 rounded-3xl w-full h-full p-8 lg:w-[983px] lg:h-[684px]">
				<div className="flex flex-col lg:flex-row justify-between w-full mb-4 gap-y-6">
					<Button
						label="Download"
						size="fit"
						decoration="secondary"
						className={clsx('bg-white', {
							hidden: !hasDownload,
						})}
						onClick={downloadImage}
					/>
					<div
						className={clsx('flex flex-col lg:flex-row items-center gap-6', {
							'justify-end w-full': !hasDownload,
						})}
					>
						<Button
							label="Cancel"
							size="fit"
							decoration="line-primary"
							onClick={hideImageEditor}
						/>
						<Button
							label="Apply Changes"
							size="fit"
							decoration="primary"
							onClick={applyChangesOnClick}
						/>
					</div>
				</div>
				<div className="relative w-full  lg:h-[551px]">
					{imageLoaded && (
						<div
							className="canvas-container m-auto"
							style={{
								width: width || '100%',
								height: height || '100%',
							}}
						>
							{isCropping ? (
								<Cropper
									image={croppableImage || ''}
									crop={crop}
									zoom={zoom}
									aspect={aspectRatio}
									onCropChange={setCrop}
									onCropComplete={(_, croppedAreaPixels) =>
										setCroppedAreaPixels(croppedAreaPixels)
									}
									onZoomChange={setZoom}
								/>
							) : (
								<canvas
									ref={canvasRef}
									onMouseDown={handlePointerDown}
									onMouseMove={handlePointerMove}
									onMouseUp={handlePointerUp}
									onWheel={handleWheel}
									style={{
										width: width || '100%',
										height: height || '100%',
									}}
									className="object-contain"
								/>
								// <CropperImage
								// 	aspectRatio={aspectRatio}
								// 	imageDefault={image}
								// 	setImageCrop={handleCropImage}
								// />
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

interface EditorOptionProps {
	title: string;
	options: {
		icon: string;
		name: string;
		value: boolean | number;
		onClick: (value: any) => void;
	}[];
}

const EditorOption: React.FC<EditorOptionProps> = ({ title, options }) => {
	const [clicked, setClicked] = React.useState<boolean>(false);
	return (
		<div>
			<Typography type="regular-14" className="text-opacity-black text-start">
				{title}
			</Typography>
			<div className="flex gap-x-7 mt-2">
				{options.map((item, index) => (
					<React.Fragment key={'editor-option-' + index}>
						<button
							type="button"
							onClick={() => {
								item.onClick(clicked);
								setClicked(!clicked);
							}}
						>
							<Icon src={item.icon} className="!size-6" />
						</button>
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

const EditorOptionRange: React.FC<{
	title: string;
	value: number;
	onChange: (value: any) => void;
}> = ({ title, value, onChange }) => {
	const { register, watch, setValue } = useForm({ mode: 'onChange' });

	React.useEffect(() => {
		if (title === 'Grayscale') {
			setValue(title, value || 0);
		} else {
			setValue(title, value || 100);
		}
	}, [value]);

	return (
		<div>
			<label>
				<Typography type="regular-14" className="text-opacity-black text-start">
					{title}
				</Typography>
			</label>
			<div className="flex items-center justify-between w-full gap-x-1.5">
				<Input
					name={title}
					register={register}
					type="range"
					min="0"
					max="200"
					classNameInput="!mt-0"
					className="w-full"
					onChangeCustom={(e: any) => onChange(e.target.value)}
				/>
				<span className="border border-opacity-primary rounded-lg py-1 px-2 h-7 w-11">
					<Typography
						type="regular-12"
						className="text-opacity-black text-center"
					>
						{watch(title) || '0'}
					</Typography>
				</span>
			</div>
		</div>
	);
};
